import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowUp, ArrowDown, ClipboardList } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type Status = "queued" | "scraping" | "classifying" | "completed" | "failed";

type AuditRow = {
  id: string;
  email: string;
  status: Status;
  inputAsins: string[];
  scrapedReviewCount: number;
  flaggedCount: number;
  createdAt: string;
};

type SortKey = "createdAt" | "asinCount" | "scrapedReviewCount" | "flaggedCount" | "status";

export default function DashboardAudits() {
  return <AppShell>{() => <Inner />}</AppShell>;
}

function Inner() {
  const [audits, setAudits] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let cancelled = false;
    api<{ audits: AuditRow[] }>("/customer/audits").then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error ?? "Failed to load audits.");
        return;
      }
      setAudits(res.data?.audits ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = useMemo(() => {
    if (!audits) return null;
    const arr = [...audits];
    arr.sort((a, b) => {
      const pick = (r: AuditRow) => {
        if (sortKey === "asinCount") return r.inputAsins.length;
        if (sortKey === "scrapedReviewCount") return r.scrapedReviewCount;
        if (sortKey === "flaggedCount") return r.flaggedCount;
        if (sortKey === "status") return r.status;
        return r.createdAt;
      };
      const av = pick(a);
      const bv = pick(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [audits, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "createdAt" ? "desc" : "desc");
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Audits
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            One-shot audits you've run.
          </p>
        </div>
        <Link
          to="/audit"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          New audit
        </Link>
      </div>

      {error && <p className="mt-6 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {sorted === null && !error && (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {sorted && sorted.length === 0 && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-900">
          <ClipboardList className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            No audits yet.
          </p>
          <Link
            to="/audit"
            className="mt-4 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            Run your first audit
          </Link>
        </div>
      )}

      {sorted && sorted.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <Th label="Date" k="createdAt" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <Th label="ASINs" k="asinCount" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <Th label="Reviews" k="scrapedReviewCount" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <Th label="Flagged" k="flaggedCount" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <Th label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {sorted.map((a) => (
                <tr
                  key={a.id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  onClick={() => {
                    window.location.href = `/dashboard/audits/${a.id}`;
                  }}
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/audits/${a.id}`}
                      className="font-medium text-slate-900 hover:underline dark:text-slate-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {new Date(a.createdAt).toLocaleDateString()}
                    </Link>
                    <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                      {a.inputAsins.slice(0, 3).join(", ")}
                      {a.inputAsins.length > 3 ? ", …" : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {a.inputAsins.length}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {a.scrapedReviewCount}
                  </td>
                  <td className="px-4 py-3">
                    {a.flaggedCount > 0 ? (
                      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
                        {a.flaggedCount}
                      </span>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={a.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onClick: (k: SortKey) => void;
}) {
  const active = k === sortKey;
  return (
    <th className="px-4 py-2.5 text-left">
      <button
        type="button"
        onClick={() => onClick(k)}
        className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100"
      >
        {label}
        {active && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function StatusBadge({ status }: { status: Status }) {
  if (status === "completed") {
    return (
      <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900">
        Completed
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900">
      In queue
    </span>
  );
}
